/**
 * NodeHeroIllustration.tsx — hero-illustrasjon for /nodejs
 *
 * Tre isometriske plattformer koblet med prikkede linjer: kodevindu (koden),
 * deploy-kjede (bygg → test → live) og server-rack (kjøring). Samme stil og
 * temafarger som de andre produktsidene; kun blå og grå aksenter.
 */

import { useId } from 'react';
import { Rack } from '@/components/invps/parts';
import { Box, iso } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';
import { CodeWindow, Pipeline } from './parts';

export default function NodeHeroIllustration({ label }: { label: string }) {
  const uid = useId();
  const blue = `${uid}-blue`;

  const A = { x: -2.5, y: 0.5 }; // kode
  const B = { x: -2.5, y: -5.5 }; // deploy-kjede
  const C = { x: 3.5, y: -3.5 }; // server
  const size = 5;

  const [badgeX, badgeY] = iso(B.x + 4.3, B.y + 1.5, 2.2).split(',').map(Number);

  return (
    <svg className={styles.scene} viewBox="110 40 660 420" role="img" aria-label={label}>
      <defs>
        <linearGradient id={blue} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4589ff" />
          <stop offset="1" stopColor="#0043ce" />
        </linearGradient>
      </defs>
      <polyline className={styles.link} points={`${iso(A.x + 2, A.y, 0)} ${iso(B.x + 2.5, B.y + size, 0)}`} />
      <polyline className={styles.link} points={`${iso(B.x + size, B.y + 3, 0)} ${iso(C.x + 0.5, C.y, 0)}`} />
      <polyline className={styles.link} points={`${iso(A.x + size, A.y + 2, 0)} ${iso(C.x, C.y + 4.5, 0)}`} />

      {/* Bakerst: deploy-kjede */}
      <Box x={B.x} y={B.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <Pipeline x={B.x + 0.4} y={B.y + 1.6} z={0.3} />
      <g transform={`translate(${badgeX} ${badgeY})`}>
        <circle r="20" fill={`url(#${blue})`} />
        <polyline points="-9,0 -3,7 10,-7" fill="none" stroke="#ffffff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Høyre: server-rack */}
      <Box x={C.x} y={C.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <Rack x={C.x + 1.2} y={C.y + 1.3} z={0.3} units={3} />

      {/* Foran: kodevindu */}
      <Box x={A.x} y={A.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <CodeWindow x={A.x + 0.6} y={A.y + 1.1} z={0.3} />
    </svg>
  );
}
